/**
 * Ответ сотрудника на вопрос клиента — страница вопроса (`/knowledge/q/<id>`, в том числе очередь) и
 * «Пробелы». Спека `docs/specs/2026-10-26-kb-questions-ux-design.md`.
 *
 * Кнопки «Сохранить» нет: текст сохраняется сам через секунду после паузы в наборе (и при уходе из
 * поля), черновик каждую букву лежит в браузере — перезагрузка страницы или обрыв связи его не теряют.
 * Голос — главная кнопка: запись после «Стоп» сразу уходит на сервер файлом (общий голосовой ввод),
 * расшифровка приходит фоном под плеером, в поле текста её не вставляем; ждать её не нужно — можно
 * сразу к следующему вопросу. Не привязалась загруженная запись — повтор берёт тот же `voice_id`.
 * Переход (следующий / назад / закрыть) ждёт сохранения текста и не уходит, пока идёт запись.
 * Клавиши (с `hotkeys`, вне полей ввода): R — запись / стоп, J — следующий, K — назад.
 * «Мой ответ полный» — отметка сотрудника, по ней отбор в «Вопросах». Правки за 10 минут — одна
 * редакция («История правок»). Карточку по ответам собирает модель в чате сессии (`llm kb-answer-card`).
 */
import { useGetIdentity } from "@refinedev/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Mic } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { AnswerHistory, VoiceNote } from "@/components/knowledge/AnswerList";
import { useVoice, VoiceControl, voiceBusy } from "@/components/voice/VoiceInput";
import {
  attachAnswerVoice,
  deleteAnswerVoice,
  loadAnswers,
  markAnswerDone,
  type QuestionAnswer,
  saveAnswer,
} from "@/lib/knowledge";
import type { CurrentUser } from "@/lib/providers";
import { uploadVoice } from "@/lib/voice";
import { btn, btnMain, errText, input } from "@/pages/KnowledgeCardPage";

/** Свой ответ, который ещё можно править. */
export function myAnswer(answers: QuestionAnswer[], meId: number | undefined): QuestionAnswer | undefined {
  return answers.find((a) => a.user_id === meId && a.status === "new");
}

const ПАУЗА = 1000;

type Draft = { text: string; at: number };
const ключ = (me: number | undefined, q: number) => `kb-answer-draft:${me ?? 0}:${q}`;

function черновик(k: string): Draft | null {
  try {
    const v = localStorage.getItem(k);
    return v ? (JSON.parse(v) as Draft) : null;
  } catch {
    return null;
  }
}
function запомнить(k: string, d: Draft | null) {
  try {
    if (d) localStorage.setItem(k, JSON.stringify(d));
    else localStorage.removeItem(k);
  } catch {
    // приватное окно или запрет хранилища: остаётся автосохранение на сервер
  }
}
/** Дата сервера — московская без пояса. */
const мск = (s: string | undefined) => (s ? new Date(`${s}+03:00`).getTime() : 0);

/** Нажатие пришло из поля ввода или элемента управления — буквенные клавиши не перехватываем. */
const вПоле = (t: EventTarget | null) =>
  t instanceof HTMLElement && !!t.closest("input,textarea,select,button,a,summary,[contenteditable='true']");

export function AnswerForm({
  questionId,
  onNext,
  onPrev,
  onClose,
  nextLabel = "Следующий вопрос",
  position,
  autoFocus,
  autoRecord,
  hotkeys,
}: {
  questionId: number;
  /** Есть — кнопка «Следующий вопрос» (переход после сохранения). */
  onNext?: (() => void) | undefined;
  /** Есть — кнопка «Назад» (очередь). */
  onPrev?: (() => void) | undefined;
  onClose?: (() => void) | undefined;
  nextLabel?: string;
  /** Подпись очереди, например «3 из 41». */
  position?: string | undefined;
  autoFocus?: boolean;
  /** Сразу начать запись голоса. */
  autoRecord?: boolean;
  /** Клавиши R / J / K вне полей ввода. */
  hotkeys?: boolean;
}) {
  const qc = useQueryClient();
  const { data: me } = useGetIdentity<CurrentUser>();
  const key = ["kb", "answers", questionId];
  const q = useQuery({
    queryKey: key,
    queryFn: () => loadAnswers(questionId),
    // пока запись расшифровывается — подтягиваем текст каждые 2 с
    refetchInterval: (query) =>
      myAnswer(query.state.data ?? [], me?.id)?.voices.some((v) => v.status === "wait" || v.status === "work") ? 2000 : false,
  });
  const мой = q.data ? myAnswer(q.data, me?.id) : undefined;
  const dk = ключ(me?.id, questionId);

  const [text, setText] = useState<string | null>(null);
  const [state, setState] = useState<{ kind: "idle" | "dirty" | "saving" | "saved" | "error"; msg?: string }>({ kind: "idle" });
  const [error, setError] = useState("");
  const latest = useRef("");
  const savedText = useRef("");
  const timer = useRef<number | null>(null);
  const inFlight = useRef<Promise<boolean> | null>(null);
  const area = useRef<HTMLTextAreaElement | null>(null);
  // запись уже загружена, а привязка к ответу не прошла — повтор не грузит файл второй раз
  const загружено = useRef(new WeakMap<Blob, number>());

  // Мой ответ из базы (или черновик браузера, если он новее) — один раз, дальше поле ведёт сотрудник.
  useEffect(() => {
    if (text !== null || !q.data || !me) return;
    const сервер = мой?.text ?? "";
    const d = черновик(dk);
    savedText.current = сервер;
    if (d && d.text !== сервер && d.at > мск(мой?.date)) {
      latest.current = d.text;
      setText(d.text);
      setState({ kind: "dirty", msg: "восстановлен несохранённый черновик" });
      schedule();
    } else {
      if (d) запомнить(dk, null);
      latest.current = сервер;
      setText(сервер);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data, me]);

  const refreshLists = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ["kb", "questions"] });
    void qc.invalidateQueries({ queryKey: ["kb", "question"] });
    void qc.invalidateQueries({ queryKey: ["kb", "gaps"] });
  }, [qc]);

  const put = (a: QuestionAnswer) =>
    qc.setQueryData<QuestionAnswer[]>(key, (old) => {
      const rest = (old ?? []).filter((x) => x.id !== a.id);
      return [...rest, a].sort((x, y) => x.id - y.id);
    });

  /** Сохранить текущий текст, если он отличается от сохранённого; по очереди, без гонок.
   * `false` — не сохранилось (черновик остался в браузере), уходить с формы нельзя. */
  const flush = useCallback(async (): Promise<boolean> => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
    if (inFlight.current) await inFlight.current;
    const t = latest.current;
    if (t.trim() === savedText.current.trim()) {
      setState((s) => (s.kind === "dirty" ? { kind: "saved" } : s));
      return true;
    }
    setState({ kind: "saving" });
    const первый = !мой;
    const p = saveAnswer(questionId, t)
      .then((a) => {
        savedText.current = a.text;
        put(a);
        if (latest.current === t) запомнить(dk, null);
        setState(latest.current === t ? { kind: "saved" } : { kind: "dirty" });
        if (первый) refreshLists();
        return true;
      })
      .catch((e: unknown) => {
        setState({ kind: "error", msg: `${errText(e)} — черновик остался в браузере, сохраню при следующей правке` });
        return false;
      })
      .finally(() => {
        inFlight.current = null;
      });
    inFlight.current = p;
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId, dk, мой?.id]);

  function schedule() {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void flush(), ПАУЗА);
  }

  // Уходим с формы — дописать, что не успело сохраниться (черновик и так в браузере).
  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
      if (latest.current.trim() !== savedText.current.trim()) void saveAnswer(questionId, latest.current).then(refreshLists, () => undefined);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Поле — по высоте текста.
  useLayoutEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.max(el.scrollHeight + 2, 88)}px`;
  }, [text]);

  const onChange = (v: string) => {
    latest.current = v;
    setText(v);
    запомнить(dk, { text: v, at: Date.now() });
    setState({ kind: "dirty" });
    schedule();
  };

  const voice = useVoice({
    onDone: async (blob, seconds) => {
      setError("");
      await flush(); // ответ заводится один, текст и голос — в нём
      let id = загружено.current.get(blob);
      if (id === undefined) {
        id = (await uploadVoice(blob, seconds)).id;
        загружено.current.set(blob, id);
      }
      put(await attachAnswerVoice(questionId, id));
      refreshLists();
    },
  });
  // Запись сразу, как открылась форма (один раз).
  const autoStarted = useRef(false);
  useEffect(() => {
    if (autoRecord && !autoStarted.current) {
      autoStarted.current = true;
      void voice.start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRecord]);

  const remove = async (voiceId: number) => {
    setError("");
    try {
      put(await deleteAnswerVoice(voiceId));
      refreshLists();
    } catch (e) {
      setError(errText(e));
    }
  };

  const toggleDone = async () => {
    setError("");
    try {
      if (!(await flush())) return;
      put(await markAnswerDone(questionId, !мой?.done));
      refreshLists();
    } catch (e) {
      setError(errText(e));
    }
  };

  /** Уйти с формы: запись не идёт и не висит неотправленной, текст сохранился. */
  const leave = async (go?: () => void) => {
    if (voiceBusy(voice.v)) {
      setError(
        voice.v.state === "sending"
          ? "Голос ещё отправляется — секунду, потом дальше"
          : "Идёт запись — нажмите «Стоп и отправить», потом дальше",
      );
      return;
    }
    if (voice.v.state === "ready" && voice.v.blob) {
      setError("Запись не отправилась — «Отправить ещё раз» или уберите её корзиной");
      return;
    }
    setError("");
    if (!(await flush())) return;
    refreshLists();
    go?.();
  };

  // Клавиши очереди: свежие обработчики без переподписки на каждый рендер.
  const keys = useRef({ voice, leave, onNext, onPrev });
  keys.current = { voice, leave, onNext, onPrev };
  useEffect(() => {
    if (!hotkeys) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.repeat || e.isComposing || e.ctrlKey || e.metaKey || e.altKey || вПоле(e.target)) return;
      const k = keys.current;
      if (e.code === "KeyR") {
        e.preventDefault();
        if (k.voice.v.state === "recording") k.voice.stop();
        else if (k.voice.v.state === "idle" || k.voice.v.state === "error") void k.voice.start();
      } else if (e.code === "KeyJ" && k.onNext) {
        e.preventDefault();
        void k.leave(k.onNext);
      } else if (e.code === "KeyK" && k.onPrev) {
        e.preventDefault();
        void k.leave(k.onPrev);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [hotkeys]);

  if (text === null) return <div className="py-2 text-[12px] text-fg-muted">{q.error ? errText(q.error) : "Загрузка…"}</div>;
  const есть = !!мой && (!!мой.text || мой.voices.length > 0);
  const свободен = voice.v.state === "idle" || voice.v.state === "error";
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {свободен ? (
          <button
            type="button"
            className={`${btnMain} flex items-center gap-1.5`}
            onClick={() => void voice.start()}
            disabled={!!мой && мой.voices.length >= 20}
            title="Надиктовать ответ (до 4 минут): после «Стоп» запись уйдёт сама, текст расшифруется фоном"
          >
            <Mic size={14} /> Ответить голосом
          </button>
        ) : (
          <VoiceControl voice={voice} sentText="Запись сохранена — текст появится под ней сам" />
        )}
        <span className="text-[11.5px] text-fg-faint" aria-live="polite">
          {voice.v.state === "error"
            ? <span className="text-err">{voice.v.error}</span>
            : свободен && voice.v.sent > 0
              ? "Запись сохранена — текст появится под ней сам, можно дальше"
              : свободен
                ? "или напишите ниже · сохраняется само"
                : ""}
        </span>
      </div>
      <textarea
        ref={area}
        className={`${input} min-h-20 resize-none overflow-hidden`}
        placeholder="Как вы отвечаете клиенту на этот вопрос"
        aria-label="Мой ответ текстом"
        value={text}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => void flush()}
      />
      {мой && мой.voices.length > 0 && (
        <div className="space-y-1.5">
          {мой.voices.map((v) => (
            <VoiceNote key={v.id} v={v} onDelete={() => void remove(v.id)} />
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={мой?.done ? `${btn} border-ok text-ok` : btn}
          aria-pressed={!!мой?.done}
          disabled={!есть && !text.trim()}
          onClick={() => void toggleDone()}
          title="Отметьте, когда ответили на вопрос полностью — по отметке есть отбор в «Вопросах»"
        >
          {мой?.done ? "✓ Мой ответ полный" : "Мой ответ полный"}
        </button>
        <span className={`text-[11.5px] ${state.kind === "error" ? "text-err" : "text-fg-faint"}`} role="status" aria-live="polite">
          {state.kind === "saving" && "Сохраняю…"}
          {state.kind === "dirty" && (state.msg ?? "Изменения сохранятся сами")}
          {state.kind === "saved" && "Сохранено"}
          {state.kind === "error" && state.msg}
        </span>
        <span className="ml-auto flex flex-wrap items-center gap-2">
          {position && <span className="text-[11.5px] tabular-nums text-fg-muted">{position}</span>}
          {onPrev && (
            <button type="button" className={btn} onClick={() => void leave(onPrev)} title="Предыдущий вопрос (K)">
              ← Назад
            </button>
          )}
          {onNext && (
            <button type="button" className={btnMain} onClick={() => void leave(onNext)} title={hotkeys ? "Следующий вопрос (J)" : undefined}>
              {nextLabel}
            </button>
          )}
          {onClose && (
            <button type="button" className={btn} onClick={() => void leave(onClose)}>
              Закрыть
            </button>
          )}
        </span>
      </div>
      {error && (
        <div className="text-[12px] text-err" role="alert">
          {error}
        </div>
      )}
      {hotkeys && (
        <div className="hidden text-[11px] text-fg-faint md:block">
          Клавиши вне поля: <kbd>R</kbd> запись / стоп · <kbd>J</kbd> следующий · <kbd>K</kbd> назад
        </div>
      )}
      {мой && <AnswerHistory answerId={мой.id} count={мой.revisions} />}
    </div>
  );
}
